import tokenEvents, { TOKEN_EVENTS } from './tokenEvents';
import appInsightsService from '@/utils/appInsights';

/**
 * TokenRefreshManager - Singleton class that manages token refresh logic
 * Coordinates between app and Service Worker for automatic token renewal
 */
class TokenRefreshManager {
  constructor() {
    this.worker = null;
    this.refreshCallback = null;
    this.currentUserId = null;
    this.refreshTimer = null;
    this.isInitialized = false;
    this.isRefreshing = false;
  }

  /**
   * Initializes the token refresh manager and registers Service Worker
   * @returns {Promise<boolean>} Success status
   */
  async init() {
    if (this.worker) return true;
    if (!navigator.serviceWorker) {
      appInsightsService.trackException(
        new Error('Service Workers not supported'),
        {
          errorType: 'WORKER_NOT_SUPPORTED',
          browser: navigator.userAgent,
          isServiceWorkerSupported: false,
          errorReason: 'Service Workers not available in this browser',
          fallbackUsed: true,
        },
        2, // Warning severity - expected in some browsers
      );

      this.isInitialized = true;
      return true;
    }

    try {
      const workerURL = '/tokenRefreshWorker.js';
      const registration = await navigator.serviceWorker.register(workerURL, { scope: '/' });

      if (registration.active) {
        this.worker = registration.active;
      } else {
        // Wait for worker to activate with timeout
        const activationTimeout = 10000; // 10 seconds
        const activationPromise = new Promise((resolve, reject) => {
          const worker = registration.installing || registration.waiting;
          if (!worker) {
            reject(new Error('No worker available for activation'));
            return;
          }

          const timeoutId = setTimeout(() => {
            reject(new Error('Worker activation timeout'));
          }, activationTimeout);

          worker.addEventListener('statechange', () => {
            if (worker.state === 'activated') {
              clearTimeout(timeoutId);
              this.worker = worker;
              resolve();
            } else if (worker.state === 'redundant') {
              clearTimeout(timeoutId);
              reject(new Error('Worker became redundant during activation'));
            }
          });
        });

        await activationPromise;
      }

      // Setup message handler
      navigator.serviceWorker.addEventListener('message', this.handleWorkerMessage.bind(this));
      this.isInitialized = true;
      return true;
    } catch (error) {
      const isRegistrationError =
        error.message?.includes('register') || error.name === 'SecurityError';

      const isActivationError =
        error.message?.includes('activation') || error.message?.includes('timeout');

      appInsightsService.trackException(
        error,
        {
          errorType: 'WORKER_REGISTRATION_FAILED',
          browser: navigator.userAgent,
          isServiceWorkerSupported: !!navigator.serviceWorker,
          errorReason: error.message || 'Unknown registration error',
          errorName: error.name,
          workerState: this.worker?.state || 'unknown',
          isRegistrationError,
          isActivationError,
          isRedundantError: error.message?.includes('redundant'),
          securityContext:
            error.name === 'SecurityError' ? 'Possible HTTPS or permissions issue' : undefined,
          fallbackUsed: true,
          workerURL: '/tokenRefreshWorker.js',
        },
        3,
      );

      this.isInitialized = true;
      return true;
    }
  }

  /**
   * Handles messages from Service Worker
   * Triggers refresh callback when refresh is needed
   */
  handleWorkerMessage = (event) => {
    const { type, payload } = event.data;
    if (
      type === 'REFRESH_TOKEN_REQUEST' &&
      payload.userId === this.currentUserId &&
      this.refreshCallback
    ) {
      this.executeTokenRefresh(payload.userId);
    }
  };

  /**
   * Executes token refresh and emits global events
   * @param {string} userId - User identifier
   */
  async executeTokenRefresh(userId) {
    if (this.isRefreshing) return;

    this.isRefreshing = true;

    try {
      if (this.refreshCallback) {
        await this.refreshCallback(userId);
      }
    } catch (error) {
      tokenEvents.emit(TOKEN_EVENTS.TOKEN_REFRESH_FAILED, { userId, error });
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Sets the callback function to be called when token refresh is needed
   * @param {Function} callback - Function to call for token refresh
   */
  setRefreshCallback(callback) {
    this.refreshCallback = callback;
  }

  /**
   * Starts monitoring token expiration for a user
   * @param {string} userId - User identifier
   * @param {string} token - Access token
   * @param {number} expiresAt - Token expiration timestamp
   * @returns {Promise<boolean>} Success status
   */
  async startTokenRefresh(userId, token, expiresAt) {
    this.currentUserId = userId;
    if (this.worker) {
      try {
        this.worker.postMessage({
          type: 'START_REFRESH',
          payload: { userId, token, expiresAt },
        });
        return true;
      } catch (error) {
        appInsightsService.trackException(
          error,
          {
            messageType: 'START_REFRESH',
            workerState: this.worker?.state || 'unknown',
            errorContext: 'Service Worker communication failure',
          },
          2,
        );
      }
    }

    this.startFallbackTimer(userId, expiresAt);
    return true;
  }

  /**
   * Fallback timer implementation when Service Worker is unavailable
   * @param {string} userId - User Identifier
   * @param {number} expiresAt - Token expiration timestamp
   */
  startFallbackTimer(userId, expiresAt) {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);

    const timeUntilRefresh = expiresAt - Date.now() - 5 * 60 * 1000;

    if (timeUntilRefresh > 0) {
      this.refreshTimer = setTimeout(() => {
        this.executeTokenRefresh(userId);
      }, timeUntilRefresh);
    }
  }

  /**
   * Updates token information after successful refresh
   * @param {string} userId - User identifier
   * @param {string} token - New access token
   * @param {number} expiresAt - New expiration timestamp
   */
  async updateToken(userId, token, expiresAt) {
    if (userId !== this.currentUserId) return;

    // Emit global event that token was refreshed
    tokenEvents.emit(TOKEN_EVENTS.TOKEN_REFRESHED, {
      userId,
      token,
      expiresAt,
      timestamp: Date.now(),
    });

    if (this.worker) {
      try {
        this.worker.postMessage({
          type: 'UPDATE_TOKEN',
          payload: { userId, token, expiresAt },
        });
      } catch (error) {
        appInsightsService.trackException(
          error,
          {
            messageType: 'UPDATE_TOKEN',
            workerState: this.worker?.state || 'unknown',
            errorContext: 'Service Worker token update failure',
          },
          2,
        );
        this.startFallbackTimer(userId, expiresAt);
      }
    } else this.startFallbackTimer(userId, expiresAt);
  }

  /**
   * Notifies that a socket reconnection is needed
   * @param {string} reason - Reason for reconnection
   */
  requestSocketReconnect(reason = 'token_refresh') {
    tokenEvents.emit(TOKEN_EVENTS.SOCKET_RECONNECT_NEEDED, {
      reason,
      timestamp: Date.now(),
      userId: this.currentUserId,
    });
  }

  /**
   * Stops token refresh monitoring and cleans up resources
   */
  async stopTokenRefresh() {
    const userId = this.currentUserId;
    this.currentUserId = null;
    this.refreshCallback = null;
    this.isRefreshing = false;

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    if (this.worker && userId) {
      try {
        this.worker.postMessage({ type: 'STOP_REFRESH', payload: { userId } });
      } catch (error) {}
    }
  }
}

export default new TokenRefreshManager();
